/**
 * What is a useful next thing to do with the file the user just produced?
 *
 * The result screen dead-ends at Download today, so the only way to keep going is to start a
 * whole new job. Every ranking rule here is derived from 14 days of production behaviour, not
 * invented — see docs/research/next-tool-transitions.md, and the resolver design in
 * docs/proposed/CHAINING_DESIGN.md.
 *
 * Two things this deliberately is NOT:
 *  - It is not `FeatureRedirectService`. That maps a *rejected input* to a recovery
 *    destination; this maps a *successful output* to a continuation. Different key,
 *    different ranking, different failure mode.
 *  - It is not a third format allowlist. It keys on the output's `toType`, the same value
 *    the result card already has.
 *
 * Phase 1 is this static table. When the `feature-transitions` collection exists, a resolver
 * can override `suggestionsFor` with measured per-page rows and keep this as the fallback for
 * pairs with no data (most of the catalog: only 8 of ~250 pages had enough volume for a
 * reliable row).
 */

export interface NextTool {
    /** Page slug, no leading slash — matches how related-tool links are emitted elsewhere. */
    slug: string;
    label: string;
    /**
     * The destination edits the image in the browser, so it can only take a format this browser
     * can decode. `/image-resizer` is a canvas editor: handed a TIFF it silently falls back to
     * its empty landing page, which is how a chained TIFF was reported as "not loaded with the
     * output image". Suggestions carrying this flag are filtered against the runtime capability
     * check before being offered.
     */
    requiresBrowserDecode?: boolean;
}

const COMPRESS_GIF: NextTool = { slug: 'compress-gif', label: 'Compress GIF' };
const COMPRESS_MP4: NextTool = { slug: 'compress-mp4', label: 'Compress MP4' };
const COMPRESS_MP3: NextTool = { slug: 'compress-mp3', label: 'Compress MP3' };
const COMPRESS_PDF: NextTool = { slug: 'compress-pdf', label: 'Compress PDF' };
const GIF_TO_MP4: NextTool = { slug: 'convert-gif-to-mp4', label: 'GIF to MP4' };
const GIF_TO_WEBP: NextTool = { slug: 'convert-gif-to-webp', label: 'GIF to WebP' };
const IMAGE_RESIZER: NextTool = { slug: 'image-resizer', label: 'Resize Image', requiresBrowserDecode: true };
const VIDEO_COMPRESSOR: NextTool = { slug: 'video-compressor', label: 'Compress Video' };
const MERGE_PDF: NextTool = { slug: 'merge-pdf', label: 'Merge PDF' };
const COMPRESS_IMAGE: NextTool = { slug: 'image-compressor', label: 'Compress Image' };

/**
 * Ranked continuations for an output type.
 *
 * gif leads with compress-gif because 86.4% of gif-output switches stay in the gif family,
 * and the two highest-volume gif-producing pages both go there (79.7% and 84.2%).
 */
const BY_TO_TYPE: Record<string, NextTool[]> = {
    gif: [COMPRESS_GIF, GIF_TO_MP4, GIF_TO_WEBP],
    mp4: [COMPRESS_MP4, VIDEO_COMPRESSOR],
    video: [COMPRESS_MP4, VIDEO_COMPRESSOR],
    m4v: [COMPRESS_MP4, VIDEO_COMPRESSOR],
    webm: [COMPRESS_MP4, VIDEO_COMPRESSOR],
    mov: [COMPRESS_MP4, VIDEO_COMPRESSOR],
    mp3: [COMPRESS_MP3],
    wav: [COMPRESS_MP3],
    audio: [COMPRESS_MP3],
    m4a: [COMPRESS_MP3],
    flac: [COMPRESS_MP3],
    pdf: [COMPRESS_PDF, MERGE_PDF],
    jpg: [COMPRESS_IMAGE, IMAGE_RESIZER],
    jpeg: [COMPRESS_IMAGE, IMAGE_RESIZER],
    png: [COMPRESS_IMAGE, IMAGE_RESIZER],
    webp: [COMPRESS_IMAGE, IMAGE_RESIZER],
    heic: [COMPRESS_IMAGE, IMAGE_RESIZER],
    tiff: [COMPRESS_IMAGE, IMAGE_RESIZER],
    image: [COMPRESS_IMAGE, IMAGE_RESIZER],
};

/** Global fallback for output types with no rule (§4 rule 7). */
const GLOBAL_FALLBACK: NextTool[] = [COMPRESS_GIF, COMPRESS_MP4];

/**
 * Ranked next tools for a finished conversion.
 *
 * @param toType   the output type from the result
 * @param currentSlug the page that produced it — always excluded, since "do the thing you
 *                 just did" is noise, not a suggestion
 * @param limit    how many to show; the result card has little room
 */
export function suggestionsFor(toType?: string | null, currentSlug?: string | null, limit = 3): NextTool[] {
    const key = (toType || '').toLowerCase();
    const ranked = BY_TO_TYPE[key] || GLOBAL_FALLBACK;
    const current = (currentSlug || '').replace(/^\//, '').toLowerCase();
    return ranked.filter(t => t.slug !== current).slice(0, limit);
}
